/**
 * Initializes an Alpine.js data object for managing individual line items in cart.
 * Key features include:
 * 1. Stock Management: Dynamically displays low stock warnings based on thresholds.
 * 2. Pricing Updates: Calculates and displays final prices, including handling discounts.
 * 3. Removal Handling: Facilitates the removal of items from the cart, with special handling for gift boxes.
 * 4. Image and Description Management: Provides enhanced image and product description handling for displayed items.
 */

const lineItemComponent = (lowStockThreshold, lowStockMessage, normalStockMessage) => ({
  lowStockThreshold,
  lowStockMessage,
  normalStockMessage,
  isRemoving: false,
  fullVariantData: {},
  finalPriceFormatted: null,

  async init() {
    setTimeout(() => {
      this.fullVariantData = this.$store.cart.lineItemProperties.find((item) => item.id === this.cartItem.variant_id);
    }, 1500);

    this.updateFinalPrice();
  },

  updateFinalPrice() {
    const { $store, cartItem } = this;
    this.finalPriceFormatted = formatPrice($store.cart.cartData.currency, cartItem.final_line_price / 100);
  },

  async removeItem(key, isGiftBox = false) {
    const { $store } = this;
    try {
      this.isRemoving = true;
      if (isGiftBox) {
        await $store.cart.clearGiftBoxAttributes();
      }
      await $store.cart.removeLineItem(key);
      // this.$dispatch('cart-items-changed');
      window.dispatchEvent(new CustomEvent('cart-items-changed'));
    } catch {
      alert('sorry, there was an error removing this item from your cart');
    } finally {
      this.isRemoving = false;
    }
  },

  get imageUrl() {
    return this.cartItem.featured_image.url;
  },
  get imageAlt() {
    return this.cartItem.featured_image.alt;
  },
  get imageWidth() {
    return this.cartItem.featured_image.width;
  },
  get imageHeight() {
    return this.cartItem.featured_image.height / 13;
  },
  get titleText() {
    const productType = this.cartItem.product_type;
    if (productType === 'Hanging Chair Hammock') {
      return 'Chair Hammock';
    } else if (productType === 'Cotton Rope Hammock') {
      return productType;
    } else if (productType === 'Classic Double Hammock') {
      return 'Signature Hammock';
    } else return this.cartItem.product_title;
  },
  get isHammockType() {
    const productType = this.cartItem.product_type;
    const hammockTypes = ['Hanging Chair Hammock', 'Cotton Rope Hammock', 'Classic Double Hammock'];

    return hammockTypes.includes(productType);
  },
  get processedOptions() {
    return this.cartItem.options_with_values
      .filter((option) => !option.value.toLowerCase().includes('default'))
      .map((option) => ({
        name: option.name.toLowerCase().includes('size') ? 'Size' : option.name,
        value: option.value,
      }));
  },
  get hammockColor() {
    if (this.isHammockType) {
      const shortTitle = this.fullVariantData.shortTitle;
      return shortTitle?.includes('/') ? shortTitle.split('/')[0] : shortTitle;
    }
    return null;
  },
  get isLowStock() {
    const { fullVariantData, lowStockThreshold } = this;
    if (fullVariantData.quantityAvailable) {
      return fullVariantData.quantityAvailable <= lowStockThreshold;
    } else return true;
  },

  get discountStatus() {
    const { fullVariantData, cartItem } = this;
    if (cartItem.discounts.length) {
      return 'APPLIED';
    } else if (
      fullVariantData.compareAtPrice &&
      parseFloat(fullVariantData.compareAtPrice.amount) > parseFloat(fullVariantData.price.amount)
    ) {
      return 'MARKDOWN';
    }

    return null;
  },
  get originalPrice() {
    const { discountStatus, fullVariantData, $store, cartItem } = this;
    if (discountStatus === 'MARKDOWN') {
      const compareAtPrice = parseFloat(fullVariantData.compareAtPrice.amount);
      const { quantity } = cartItem;

      return formatPrice($store.cart.cartData.currency, compareAtPrice * quantity);
    } else if (discountStatus === 'APPLIED') {
      return formatPrice($store.cart.cartData.currency, cartItem.original_line_price / 100);
    }
    return '';
  },

  get finalPrice() {
    const { $store, cartItem } = this;
    return formatPrice($store.cart.cartData.currency, cartItem.final_line_price / 100);
  },
});

document.addEventListener('alpine:init', () => {
  Alpine.data('lineItemComponent', lineItemComponent);
});
